<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>
            Dispatch User Detail
          </h2>
        </div>
      </div>
    </div>


    <div class="col-12">
      <div class="card">
        <div class="card-header d-flex justify-content-between gap-2">
          <h3>
            <span v-if="isNewDispatchUser">New </span>
            <span v-else-if="editUserInfo">Edit </span>
            Dispatch User Information
          </h3>

          <div class="d-flex gap-2" v-if="!isNewDispatchUser">
            <button class="btn btn-primary" @click="showResetPasswordConfirmation">
              <i class="fa-solid fa-key"></i>
              Reset Password
            </button>
            <button v-if="userData && !userData.lastLogin" class="btn btn-success" @click="showSendInviteConfirmation">
              <i class="fa-solid fa-envelope"></i>
              Send Registration Invite
            </button>
            <button v-if="!editUserInfo" class="btn btn-outline-primary" @click="toggleEditMode">
              <i class="icon-pencil-alt"></i>
              Edit Information
            </button>
          </div>
        </div>

        <div class="card-body">
          <div v-if="!editUserInfo && !isNewDispatchUser">
            <div class="mb-3">
              <span v-if="userData && userData.fullName" class="f-w-700 fs-5"> {{ userData.fullName }}</span>
            </div>

            <div class="row align-items-center gy-4 mt-2">
              <div class="col-md-6">
                <ul>
                  <li class="d-flex align-items-center pb-2 fs-6">
                    <span class="f-w-700">Email:</span> &nbsp;
                    <span v-if="userData && userData.email"> {{ userData.email }} </span>
                  </li>

                  <li class="d-flex align-items-center py-2 fs-6">
                    <span class="f-w-700">Mobile Number:</span> &nbsp;
                    <span v-if="userData && userData.phoneNumber"> {{ userData.phoneNumber }} </span>
                  </li>

                  <li class="d-flex align-items-center pt-2 fs-6">
                    <span class="f-w-700">Service Provider:</span> &nbsp;
                    <span v-if="userData && userData.serviceProvider">
                      {{ userData.serviceProvider.name }}
                    </span>
                  </li>
                </ul>
              </div>

              <div class="col-md-6 border-start">
                <ul>
                  <li class="d-flex align-items-center gap-2 pb-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Email Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="userData && userData.emailVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 py-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Mobile Verification</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="userData && userData.phoneNumberVerified"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 py-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Admin User</h5>
                    </div>
                    <div class="recent-text">
                      <h5>
                        <i v-if="userData && userData.isAdminUser"
                           class="fa-solid fa-circle-check text-success"></i>
                        <i v-else class="fa-solid fa-circle-xmark text-danger"></i>
                      </h5>
                    </div>
                  </li>
                  <li class="d-flex align-items-center gap-2 pt-2">
                    <div class="flex-grow-1">
                      <h5 class="f-w-400">Last Login</h5>
                    </div>
                    <div class="recent-text">
                      <h5 v-if="userData && userData.lastLogin">
                        {{ formatTime(userData.lastLogin) }}
                      </h5>
                      <h5 v-else>N/A</h5>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="editUserInfo || isNewDispatchUser">
            <EntityForm model-constructor="DispatchUser"
                        :api-url= "`/api/${props.serviceProviderId}/dispatch-user`"
                        v-model:entity="userFormData"
                        form-name="Dispatch User"
                        :show-title="false"
                        :start-in-edit="true"
                        :entity-id="userId"
                        :post-save="saveCallback"
                        :on-cancel-callback="cancelCallback"
                        :redirect-url="`/service-provider/${props.serviceProviderId}/dispatch-user`">

              <div v-if="userFormData.state">
                <div class="row">
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="dispatch-user-first-name-input">First Name</label>
                      <input id="dispatch-user-first-name-input" v-model="userFormData.state.firstName"
                             class="form-control" type="text" placeholder="John"/>
                      <div id="dispatch-user-first-name-error" class="invalid-feedback d-none">Please enter the first
                        name.
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="dispatch-user-last-name-input">Last Name</label>
                      <input id="dispatch-user-last-name-input" v-model="userFormData.state.lastName"
                             class="form-control" type="text" placeholder="Doe"/>
                      <div id="dispatch-user-last-name-error" class="invalid-feedback d-none">
                        Please enter the last name.
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="dispatch-user-email-input">Email</label>
                      <input id="dispatch-user-email-input" v-model="userFormData.state.email" class="form-control"
                             type="text" placeholder="email@example.com"/>
                      <div id="dispatch-user-email-error" class="invalid-feedback d-none">Please enter a valid email
                        address.
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="dispatch-user-phone-number-input">Phone Number</label>
                      <input id="dispatch-user-phone-number-input" v-model="userFormData.state.phoneNumber"
                             class="form-control"
                             type="text" placeholder="+1234567890"/>
                      <div id="dispatch-user-phone-number-error" class="invalid-feedback d-none">Please enter a valid phone
                        number.
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="user-admin-input">Admin User</label>
                      <div class="form-check form-switch">
                        <input id="user-admin-input" v-model="userFormData.state.isAdminUser"
                               class="form-check-input" type="checkbox"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </EntityForm>
          </div>
        </div>
      </div>
    </div>


  </div>

</template>

<script setup>
import {onMounted, ref, nextTick} from "vue";
import moment from "moment";
import {format} from "timeago.js";
import EntityForm from "./EntityForm.vue";
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import {showToast} from "../utility/toastNotificationUtility";
import 'jquery-ui';
import {renderWithPoi} from '../utility/addressHelper';
import {newEntityId} from '../globals.js';

const props = defineProps({
  serviceProviderId: {type: String, required: true},
  userId: { type: String, default: newEntityId }
});

const userData = ref(undefined);
const editUserInfo = ref(false);
const userFormData = ref({});
const isProcessing = ref(false);
const isNewDispatchUser = ref(props.userId === newEntityId);



const formatTime = (value) => {
  return `${moment(value).format('MMM DD, YYYY')} (${format(new Date(value))})`
};

/*
* Form edit functionality
* */
const toggleEditMode = () => {
  editUserInfo.value = !editUserInfo.value;
}

const cancelCallback = () => {
  toggleEditMode();
}

const saveCallback = () => {
  loadUserData();
  toggleEditMode();
}

const loadUserData = async () => {
  if (isNewDispatchUser.value) {
    editUserInfo.value = true;
    return;
  }

  const response = await fetch(`/api/${props.serviceProviderId}/dispatch-user/${props.userId}`);

  if (response.ok) {
    userData.value = await response.json();
  }
}

const showResetPasswordConfirmation = () => {
  if (!userData.value) return;

  // Show the reset password confirmation using SweetAlert
  Swal.fire({
    title: 'Reset Password',
    text: `Are you sure you want to send a password reset email for ${userData.value.fullName}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Reset Password',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33'
  }).then((result) => {
    if (result.isConfirmed) {
      resetPassword();
    }
  });
};

const resetPassword = async () => {
  if (!userData.value) return;

  isProcessing.value = true;

  try {
    // Call the API endpoint to send a password reset email
    const response = await fetch(`/api/authenticate/send-password-reset/${userData.value.id}`, {
      method: 'POST'
    });

    if (response.ok) {
      // Show success message
      showToast({
        title: 'success',
        body: `Password reset email for ${userData.value.fullName} has been sent.`,
        type: 'success',
        delay: 7000
      });
    } else {
      // Get the error message from the response
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send password reset email');
    }
  } catch (error) {
    console.error('Error resetting password:', error);

    // Show error message
    showToast({
      title: 'error',
      body: error.message || 'There was an error sending the password reset email. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

const showSendInviteConfirmation = () => {
  if (!userData.value) return;

  // Check if user has an email
  if (!userData.value.email) {
    showToast({
      title: 'error',
      body: 'This user does not have an email address. Please add an email address before sending an invite.',
      type: 'error',
      delay: 7000
    });
    return;
  }

  // Show the send invite confirmation using SweetAlert
  Swal.fire({
    title: 'Send Registration Invite',
    text: `Are you sure you want to send a registration invite to ${userData.value.fullName} at ${userData.value.email}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Send Invite',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d'
  }).then((result) => {
    if (result.isConfirmed) {
      sendInvite();
    }
  });
};

const sendInvite = async () => {
  if (!userData.value || !userData.value.email) return;

  isProcessing.value = true;

  try {
    // Call the API endpoint to send an invitation email
    const response = await fetch(`/api/authenticate/send-invitation/${userData.value.id}`, {
      method: 'POST'
    });

    if (response.ok) {
      // Show success message
      showToast({
        title: 'success',
        body: `Registration invite has been sent to ${userData.value.email}.`,
        type: 'success',
        delay: 7000
      });
    } else {
      // Get the error message from the response
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send registration invite');
    }
  } catch (error) {
    console.error('Error sending invite:', error);

    // Show error message
    showToast({
      title: 'error',
      body: error.message || 'There was an error sending the registration invite. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

onMounted(() => {
  loadUserData();
})
</script>
